'use client';

import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import {
  Globe02Icon,
  JusticeScale01Icon,
  Rocket01Icon,
  TradeUpIcon,
} from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';

interface WinnersHowItWorksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the "What is graduation?" sub-modal from an inline cross-link. */
  onWhatIsGraduation?: () => void;
}

/**
 * Winners-specific how-it-works modal. Mirrors `MarketsHowItWorksModal` on the
 * shared `InfoModal` primitive. Conveys that because a graduated winner trades
 * off-platform, outside buyers can lift the price — and the value of your shares.
 */
export function WinnersHowItWorksModal({
  open,
  onOpenChange,
  onWhatIsGraduation,
}: WinnersHowItWorksModalProps): React.ReactNode {
  const t = useTranslations('winners_how_it_works');

  return (
    <InfoModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('title')}
      description={t('description')}
      footer={<InfoModal.Footer cta={t('got_it')} onClose={(): void => onOpenChange(false)} />}
    >
      <InfoModal.IconList>
        <InfoModal.IconItem
          icon={JusticeScale01Icon}
          title={t('close_title')}
          description={t('close_description')}
        />
        <InfoModal.IconItem
          icon={Rocket01Icon}
          title={t('golive_title')}
          description={t('golive_description')}
        />
        <InfoModal.IconItem
          icon={Globe02Icon}
          title={t('open_title')}
          description={t('open_description')}
        />
        <InfoModal.IconItem
          icon={TradeUpIcon}
          title={t('grow_title')}
          description={t('grow_description')}
        />
      </InfoModal.IconList>
      <InfoModal.Callout>{t('callout')}</InfoModal.Callout>
      <DocsLinks
        docsPath="/concepts/winners"
        crossLink={
          onWhatIsGraduation !== undefined
            ? { label: t('graduation_link'), onClick: onWhatIsGraduation }
            : undefined
        }
      />
    </InfoModal>
  );
}
